<template>
  <div class="container-fluid">
    <div>
      <div class="mb-4">
        <h4>
          {{ $t("WEEKLY_REPORTS.WEEK_OF") }}:
          {{ $timeZoneDateFormat(weeklyReport.starts_at, "LL") }} -
          {{ $timeZoneDateFormat(weeklyReport.ends_at, "LL") }}
        </h4>
      </div>

      <div>
        <!-- Daily Sales Table -->
        <div class="table-responsive mb-5">
          <table class="table table-bordered table-striped weekly-report-main-table">
            <colgroup>
              <col style="width: 60%" />
              <col style="width: 20%" />
              <col style="width: 20%" />
            </colgroup>
            <tbody>
              <tr>
                <td class="text-center font-weight-bold bg-gray">
                  {{ $t("WEEKLY_REPORTS.SALES") }}
                </td>
                <td class="text-center font-weight-bold bg-gray">
                  {{ $t("WEEKLY_REPORTS.WITHOUT_TAXES") }}
                </td>
                <td class="text-center font-weight-bold bg-gray">
                  {{ $t("WEEKLY_REPORTS.WITH_TAXES") }}
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>{{ $t("WEEKLY_REPORTS.CASH_REGISTERS_SALES") }}</td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.daily_sales_total"
                    :tooltip="$t('WEEKLY_REPORTS.SRC_DAILY_SALES')"
                  />
                </td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.daily_sales_total_with_taxes"
                    :tooltip="$t('WEEKLY_REPORTS.SRC_DAILY_SALES')"
                  />
                </td>
              </tr>
              <tr>
                <td>{{ $t("WEEKLY_REPORTS.ACTUAL_RECEIVED_TOTAL") }}</td>
                <td>
                  <span class="form-control-plaintext">-</span>
                </td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.actual_received_total"
                    :tooltip="$t('WEEKLY_REPORTS.SRC_ACTUAL_RECEIVED')"
                  />
                </td>
              </tr>
              <tr>
                <td>{{ $t("WEEKLY_REPORTS.COUPONS_TO_INVOICE_TOTAL") }}</td>
                <td>
                  <span class="form-control-plaintext">-</span>
                </td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.coupons_to_invoice_total"
                    :tooltip="$t('WEEKLY_REPORTS.SRC_COUPONS_TOTAL')"
                  />
                </td>
              </tr>
              <tr>
                <td>{{ $t("WEEKLY_REPORTS.COUPONS_TO_INVOICE_REMAINING") }}</td>
                <td>
                  <span class="form-control-plaintext">-</span>
                </td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.coupons_to_invoice_remaining"
                    :tooltip="$t('WEEKLY_REPORTS.SRC_COUPONS_REMAINING')"
                  />
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>{{ $t("WEEKLY_REPORTS.INVOICING_SALES") }}</td>
                <td>
                  <weekly-report-figure
                    :value="
                      weeklyReport.invoicing_sales_total +
                      weeklyReport.extra_sales_total
                    "
                    :tooltip="$t('WEEKLY_REPORTS.SRC_INVOICING_SALES')"
                  />
                </td>
                <td>
                  <weekly-report-figure
                    :value="
                      weeklyReport.invoicing_sales_total_with_taxes +
                      weeklyReport.extra_sales_total_with_taxes
                    "
                    :tooltip="$t('WEEKLY_REPORTS.SRC_INVOICING_SALES')"
                  />
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>{{ $t("WEEKLY_REPORTS.INTERNAL_INVOICE_OUT") }}</td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.internal_invoice_out_total"
                    :tooltip="$t('WEEKLY_REPORTS.SRC_INTERNAL_INVOICE_OUT')"
                  />
                </td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.internal_invoice_out_with_taxes"
                    :tooltip="$t('WEEKLY_REPORTS.SRC_INTERNAL_INVOICE_OUT')"
                  />
                </td>
              </tr>
              <tr>
                <td class="font-weight-bold">
                  {{ $t("WEEKLY_REPORTS.VENDING_MACHINE_SALES") }}
                </td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.sales_vending_machines"
                    :tooltip="$t('WEEKLY_REPORTS.SRC_VENDING_MACHINE_SALES')"
                  />
                </td>
                <td>
                  <base-input
                    type="number"
                    v-model="vendingMachineSales"
                    :disabled="
                      !$currentUserCan($permissions.PERM_EDIT_WEEKLY_REPORTS)
                    "
                    step="0.01"
                    min="0"
                    :placeholder="$t('WEEKLY_REPORTS.WITH_TAXES')"
                    @input="debouncedUpdateVendingMachineSales"
                    @blur="updateVendingMachineSales"
                  />
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>{{ $t("WEEKLY_REPORTS.TOTAL_SALES") }}</td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.total_sales"
                    :tooltip="wrFormulaTotalSales(weeklyReport)"
                  />
                </td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.total_sales_with_taxes"
                    :tooltip="wrFormulaTotalSales(weeklyReport, true)"
                  />
                </td>
              </tr>
              <tr>
                <td class="text-center font-weight-bold bg-gray">
                  {{ $t("WEEKLY_REPORTS.PURCHASES") }}
                </td>
                <td class="text-center font-weight-bold bg-gray">
                  {{ $t("WEEKLY_REPORTS.WITHOUT_TAXES") }}
                </td>
                <td class="text-center font-weight-bold bg-gray">
                  {{ $t("WEEKLY_REPORTS.WITH_TAXES") }}
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>{{ $t("WEEKLY_REPORTS.PURCHASE_ORDERS_TOTAL") }}</td>
                <td>
                  <weekly-report-figure
                    :value="
                      weeklyReport.total_purchases +
                      weeklyReport.transfer_out_total -
                      weeklyReport.transfer_in_total -
                      weeklyReport.internal_invoice_in_total
                    "
                    :tooltip="$t('WEEKLY_REPORTS.SRC_PURCHASE_ORDERS_TOTAL')"
                  />
                </td>
                <td>
                  <weekly-report-figure
                    :value="
                      weeklyReport.total_purchases_with_taxes +
                      weeklyReport.transfer_out_with_taxes -
                      weeklyReport.transfer_in_with_taxes -
                      weeklyReport.internal_invoice_in_with_taxes
                    "
                    :tooltip="$t('WEEKLY_REPORTS.SRC_PURCHASE_ORDERS_TOTAL')"
                  />
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>{{ $t("WEEKLY_REPORTS.TRANSFER_OUT") }}</td>
                <td>
                  <weekly-report-figure
                    prefix="-"
                    :value="weeklyReport.transfer_out_total"
                    :tooltip="$t('WEEKLY_REPORTS.SRC_TRANSFER_OUT')"
                  />
                </td>
                <td>
                  <weekly-report-figure
                    prefix="-"
                    :value="weeklyReport.transfer_out_with_taxes"
                    :tooltip="$t('WEEKLY_REPORTS.SRC_TRANSFER_OUT')"
                  />
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>{{ $t("WEEKLY_REPORTS.TRANSFER_IN") }}</td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.transfer_in_total"
                    :tooltip="$t('WEEKLY_REPORTS.SRC_TRANSFER_IN')"
                  />
                </td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.transfer_in_with_taxes"
                    :tooltip="$t('WEEKLY_REPORTS.SRC_TRANSFER_IN')"
                  />
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>{{ $t("WEEKLY_REPORTS.INTERNAL_INVOICE_IN") }}</td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.internal_invoice_in_total"
                    :tooltip="$t('WEEKLY_REPORTS.SRC_INTERNAL_INVOICE_IN')"
                  />
                </td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.internal_invoice_in_with_taxes"
                    :tooltip="$t('WEEKLY_REPORTS.SRC_INTERNAL_INVOICE_IN')"
                  />
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>{{ $t("WEEKLY_REPORTS.PURCHASE_TOTAL") }}</td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.total_purchases"
                    :tooltip="wrFormulaTotalPurchases(weeklyReport)"
                  />
                </td>
                <td>
                  <weekly-report-figure
                    :value="weeklyReport.total_purchases_with_taxes"
                    :tooltip="wrFormulaTotalPurchases(weeklyReport, true)"
                  />
                </td>
              </tr>
              <tr>
                <td class="text-center font-weight-bold bg-gray">
                  {{ $t("WEEKLY_REPORTS.OTHER") }}
                </td>
                <td class="text-center font-weight-bold bg-gray">
                  {{ $t("WEEKLY_REPORTS.WITHOUT_TAXES") }}
                </td>
                <td class="text-center font-weight-bold bg-gray">
                  {{ $t("WEEKLY_REPORTS.WITH_TAXES") }}
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>
                  {{ $t("WEEKLY_REPORTS.WAGES") }}
                  <el-tooltip
                    :content="$t('WEEKLY_REPORTS.SRC_WAGES')"
                    placement="top"
                  >
                    <i class="fas fa-info-circle wr-figure-icon"></i>
                  </el-tooltip>
                </td>
                <td>
                  <base-input
                    type="number"
                    v-model="wages"
                    :disabled="
                      !$currentUserCan($permissions.PERM_EDIT_WEEKLY_REPORTS)
                    "
                    step="0.01"
                    min="0"
                    @input="debouncedUpdateWages"
                    @blur="updateWages"
                  />
                </td>
                <td>
                  <el-tooltip
                    :content="$t('WEEKLY_REPORTS.SRC_WAGES')"
                    placement="top"
                  >
                    <base-input
                      type="number"
                      :value="weeklyReport.wages_with_taxes"
                      :disabled="true"
                      step="0.01"
                      min="0"
                      :placeholder="$t('WEEKLY_REPORTS.WITH_TAXES')"
                    />
                  </el-tooltip>
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td>
                  {{ $t("WEEKLY_REPORTS.RENT") }}
                  <small v-if="rentTypeSuffix" class="text-muted">
                    ({{ rentTypeSuffix }})</small
                  >
                  <el-tooltip
                    :content="$t('WEEKLY_REPORTS.SRC_RENT')"
                    placement="top"
                  >
                    <i class="fas fa-info-circle wr-figure-icon"></i>
                  </el-tooltip>
                </td>
                <td>
                  <base-input
                    type="number"
                    v-model="rent"
                    :disabled="
                      !$currentUserCan($permissions.PERM_EDIT_WEEKLY_REPORTS)
                    "
                    step="0.01"
                    min="0"
                    @input="debouncedUpdateRent"
                    @blur="updateRent"
                  />
                </td>
                <td>
                  <el-tooltip
                    :content="$t('WEEKLY_REPORTS.SRC_RENT')"
                    placement="top"
                  >
                    <base-input
                      type="number"
                      :value="weeklyReport.rent_with_taxes"
                      :disabled="true"
                      step="0.01"
                      min="0"
                      :placeholder="$t('WEEKLY_REPORTS.WITH_TAXES')"
                    />
                  </el-tooltip>
                </td>
              </tr>
              <tr v-if="weeklyReport.weekly_rent_type === 'DISCOUNT'">
                <td colspan="3">
                  <div class="card bg-light">
                    <div class="card-body p-3">
                      <div
                        class="d-flex justify-content-between align-items-center mb-2"
                      >
                        <strong>{{ $t("WEEKLY_REPORTS.RENT") }}</strong>
                        <span>{{ $formatCurrency(weeklyReport.rent) }}</span>
                      </div>
                      <div
                        v-if="
                          rentDiscountBreakdown &&
                          rentDiscountBreakdown.breakdown &&
                          rentDiscountBreakdown.breakdown.length
                        "
                      >
                        <table class="table table-sm mb-2">
                          <thead>
                            <tr>
                              <th>{{ $t("COMMON.SOURCE") }}</th>
                              <th class="text-right">
                                {{ $t("COMMON.BASE") }}
                              </th>
                              <th class="text-right">
                                {{ $t("COMMON.RATE") }}
                              </th>
                              <th class="text-right">
                                {{ $t("COMMON.RENT_PART") }}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="item in rentDiscountBreakdown.breakdown"
                              :key="item.type"
                            >
                              <td>{{ discountTypeLabel(item.type) }}</td>
                              <td class="text-right">
                                {{ $formatCurrency(item.base) }}
                              </td>
                              <td class="text-right">
                                {{ Number(item.rate).toFixed(2) }}%
                              </td>
                              <td class="text-right">
                                {{ $formatCurrency(item.rent_part) }}
                              </td>
                            </tr>
                            <tr class="font-weight-bold">
                              <td colspan="3" class="text-right">
                                {{ $t("COMMON.TOTAL") }}
                              </td>
                              <td class="text-right">
                                {{
                                  $formatCurrency(
                                    rentDiscountBreakdown.rent_total
                                  )
                                }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div
                        v-else-if="
                          rentDiscountBreakdown &&
                          rentDiscountBreakdown.mode === 'flat'
                        "
                      >
                        <table class="table table-sm mb-2">
                          <thead>
                            <tr>
                              <th>{{ $t("COMMON.SOURCE") }}</th>
                              <th class="text-right">
                                {{ $t("COMMON.BASE") }}
                              </th>
                              <th class="text-right">
                                {{ $t("COMMON.RATE") }}
                              </th>
                              <th class="text-right">
                                {{ $t("COMMON.RENT_PART") }}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>{{ $t("WEEKLY_REPORTS.DISCOUNT") }}</td>
                              <td class="text-right">
                                {{
                                  $formatCurrency(rentDiscountBreakdown.base)
                                }}
                              </td>
                              <td class="text-right">
                                {{
                                  Number(rentDiscountBreakdown.rate).toFixed(2)
                                }}%
                              </td>
                              <td class="text-right">
                                {{
                                  $formatCurrency(
                                    rentDiscountBreakdown.rent_total
                                  )
                                }}
                              </td>
                            </tr>
                            <tr class="font-weight-bold">
                              <td colspan="3" class="text-right">
                                {{ $t("COMMON.TOTAL") }}
                              </td>
                              <td class="text-right">
                                {{
                                  $formatCurrency(
                                    rentDiscountBreakdown.rent_total
                                  )
                                }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div v-else class="text-muted">
                        {{ $t("WEEKLY_REPORTS.NO_DATA") }}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <td colspan="3" class="text-center font-weight-bold bg-gray">
                  {{ $t("WEEKLY_REPORTS.EXCERPT") }}
                </td>
              </tr>
              <tr class="font-weight-bold">
                <td colspan="3" class="excerpt-cell">
                  <html-editor
                    v-model="excerpt"
                    @input="debouncedUpdateExcerpt"
                    @blur="updateExcerpt"
                  ></html-editor>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HtmlEditor from "@/components/Inputs/HtmlEditor";
import { debounce } from "lodash";
import weeklyReportFormulasMixin from "@/mixins/weekly-report-formulas-mixin";
import WeeklyReportFigure from "./WeeklyReportFigure.vue";

export default {
  name: "weekly-report-view-daily-sales",

  mixins: [weeklyReportFormulasMixin],

  components: {
    HtmlEditor,
    WeeklyReportFigure,
  },

  props: {
    weeklyReport: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      vendingMachineSales:
        this.weeklyReport.sales_vending_machines_with_taxes || 0,
      wages: this.weeklyReport.wages || 0,
      excerpt: this.weeklyReport.excerpt || "",
      rent: this.weeklyReport.rent || 0,
      isUpdating: false,
    };
  },

  created() {
    // Create debounced versions of update methods
    this.debouncedUpdateVendingMachineSales = debounce(
      this.updateVendingMachineSales,
      1500
    );
    this.debouncedUpdateWages = debounce(this.updateWages, 1500);
    this.debouncedUpdateRent = debounce(this.updateRent, 1500);
    this.debouncedUpdateExcerpt = debounce(this.updateExcerpt, 2000);
  },

  computed: {
    rentTypeSuffix() {
      if (!this.weeklyReport || !this.weeklyReport.weekly_rent_type) return "";

      let typeLabel;
      if (this.weeklyReport.weekly_rent_type === "DISCOUNT") {
        typeLabel = this.$t("WEEKLY_REPORTS.DISCOUNT");
      } else if (this.weeklyReport.weekly_rent_type === "FIXED_AMOUNT") {
        typeLabel = this.$t("WEEKLY_REPORTS.FIXED_AMOUNT");
      } else {
        typeLabel = this.weeklyReport.weekly_rent_type;
      }

      if (this.weeklyReport.weekly_rent == null) return typeLabel;

      if (this.weeklyReport.weekly_rent_type === "DISCOUNT") {
        const discountPercentage = Number(this.weeklyReport.weekly_rent);
        const discountDisplay = isNaN(discountPercentage)
          ? this.weeklyReport.weekly_rent
          : `${discountPercentage}%`;
        return `${typeLabel} – ${discountDisplay}`;
      }
      if (this.weeklyReport.weekly_rent_type === "FIXED_AMOUNT") {
        return `${typeLabel} – ${this.$formatCurrency(
          this.weeklyReport.weekly_rent
        )}`;
      }
      return typeLabel;
    },
    rentDiscountBreakdown() {
      return this.weeklyReport.rent_discount_breakdown || null;
    },
  },

  watch: {
    weeklyReport: {
      handler(newWeeklyReport) {
        if (newWeeklyReport) {
          this.vendingMachineSales =
            newWeeklyReport.sales_vending_machines_with_taxes || 0;
          this.wages = newWeeklyReport.wages || 0;
          this.excerpt = newWeeklyReport.excerpt || "";
          this.rent = newWeeklyReport.rent || 0;
        }
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    emitWeeklyReportUpdate() {
      // Prevent multiple simultaneous updates
      if (this.isUpdating) {
        return;
      }

      this.isUpdating = true;
      // Emit update event to parent
      this.$emit("update:weeklyReport", this.weeklyReport);

      // Reset updating flag after a short delay
      setTimeout(() => {
        this.isUpdating = false;
      }, 100);
    },

    updateVendingMachineSales() {
      // Update the weeklyReport object directly and emit the change
      this.weeklyReport.sales_vending_machines_with_taxes =
        this.vendingMachineSales;
      this.emitWeeklyReportUpdate();
    },

    updateExcerpt() {
      // Update the weeklyReport object directly and emit the change
      this.weeklyReport.excerpt = this.excerpt;
      this.emitWeeklyReportUpdate();
    },

    updateRent() {
      // Update the weeklyReport object directly and emit the change
      this.weeklyReport.rent = this.rent;
      this.emitWeeklyReportUpdate();
    },

    updateWages() {
      // Update the weeklyReport object directly and emit the change
      this.weeklyReport.wages = this.wages;
      this.emitWeeklyReportUpdate();
    },

    discountTypeLabel(type) {
      const labels = {
        SALES_ORDERS: this.$t("WEEKLY_REPORTS.INVOICING_SALES"),
        SALES_SUBSIDIZED: this.$t("WEEKLY_REPORTS.SUBSIDIZED_SALES"),
        SALES_VENDING_MACHINES: this.$t("WEEKLY_REPORTS.VENDING_MACHINE_SALES"),
        DAILY_SALES_TOTAL: this.$t("WEEKLY_REPORTS.CASH_REGISTERS_SALES"),
      };
      return labels[type] || type;
    },
  },
};
</script>

<style scoped>
.table th,
.table td {
  vertical-align: middle;
  padding: 0.5rem;
}

/* Lock the main report table so a long excerpt cannot stretch the columns. */
.weekly-report-main-table {
  table-layout: fixed;
  width: 100%;
}

/* Force the excerpt cell + its rich-text contents to wrap instead of
   pushing the table wider. max-width:0 is the standard trick to make a
   td respect its parent's width in a fixed-layout table. */
.excerpt-cell {
  max-width: 0;
  width: 100%;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.excerpt-cell ::v-deep .ql-container,
.excerpt-cell ::v-deep .ql-editor {
  max-width: 100%;
  word-break: break-word;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}
</style>
